import { Button, Chip, Icon, IconButton, Input, Loading, Typography } from '@beeline.com/bp-design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AppLink } from 'src/components/common/AppLink/AppLink';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import {
  DailyBriefingGenerateDetails,
  DailyBriefingGenerateSource,
  DailyBriefingOpenReferenceDetails
} from 'src/integrations/mixpanel/Events/dailyBriefing';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { BriefingItemCategory, BriefingReferenceType, BriefingSeverity, ResponsibleParty } from 'src/graphql/types';
import { Pathname } from 'src/utils/hooks/navigation/types';
import { QueryError, useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { ResizeDrawer } from '../docsSidePanel/ResizeDrawer';
import { GetDailyBriefingDocument, GetDailyBriefingQuery } from './data/__generated__/getDailyBriefing.graphql';
import { DailyBriefingIntlMessages, useDailyBriefingIntl } from './translations/useDailyBriefingIntl';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type CategoryChipColor = 'info' | 'secondary' | 'warning' | 'success' | 'default';
type SeverityChipColor = 'warning' | 'info' | 'default';

type ActionItem = NonNullable<NonNullable<GetDailyBriefingQuery['dailyBriefing']['actionItems']>[number]>;
type Reference = NonNullable<NonNullable<ActionItem['references']>[number]>;

const REFERENCE_LIMIT = 3;

const SEVERITY_CHIP_COLOR: Record<BriefingSeverity, SeverityChipColor> = {
  [BriefingSeverity.High]: 'warning',
  [BriefingSeverity.Medium]: 'info',
  [BriefingSeverity.Low]: 'default'
};

const CATEGORY_COLOR: Record<BriefingItemCategory, CategoryChipColor> = {
  [BriefingItemCategory.Onboarding]: 'info',
  [BriefingItemCategory.Offboarding]: 'secondary',
  [BriefingItemCategory.Task]: 'warning',
  [BriefingItemCategory.Process]: 'success',
  [BriefingItemCategory.System]: 'secondary',
  [BriefingItemCategory.Compliance]: 'warning',
  [BriefingItemCategory.Other]: 'default'
};

const severityLabel = (intl: DailyBriefingIntlMessages, severity: BriefingSeverity): string =>
  ({
    [BriefingSeverity.High]: intl.severityHigh,
    [BriefingSeverity.Medium]: intl.severityMedium,
    [BriefingSeverity.Low]: intl.severityLow
  })[severity];

const partyLabel = (intl: DailyBriefingIntlMessages, party: ResponsibleParty): string =>
  ({
    [ResponsibleParty.Supplier]: intl.partySupplier,
    [ResponsibleParty.Msp]: intl.partyMsp,
    [ResponsibleParty.Person]: intl.partyPerson,
    [ResponsibleParty.Unassigned]: intl.partyUnassigned
  })[party];

const waitingOnLabels = (item: ActionItem, intl: DailyBriefingIntlMessages): string[] => {
  const roles = (item.waitingOnRoles ?? []).filter((role): role is string => !!role);
  if (roles.length > 0) {
    return roles;
  }
  const parties = (item.waitingOnParties ?? []).filter((party): party is ResponsibleParty => !!party);
  return parties.map((party) => partyLabel(intl, party));
};

const categoryLabel = (intl: DailyBriefingIntlMessages, category: BriefingItemCategory): string =>
  ({
    [BriefingItemCategory.Onboarding]: intl.categoryOnboarding,
    [BriefingItemCategory.Offboarding]: intl.categoryOffboarding,
    [BriefingItemCategory.Task]: intl.categoryTask,
    [BriefingItemCategory.Process]: intl.categoryProcess,
    [BriefingItemCategory.System]: intl.categorySystem,
    [BriefingItemCategory.Compliance]: intl.categoryCompliance,
    [BriefingItemCategory.Other]: intl.categoryOther
  })[category];

const isNavigableEngagement = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.Engagement && !!ref.uid && !!ref.personUid;

const isNavigablePerson = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.Person && !!ref.uid;

const isNavigableTask = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.Task && !!ref.uid;

const isNavigableContract = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.Contract && !!ref.uid;

const isNavigableSupplier = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.Supplier && !!ref.uid;

const isNavigableSystem = (ref: Reference | null): boolean => !!ref && ref.type === BriefingReferenceType.System;

const isNavigableWorkRequest = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.WorkRequest && !!ref.uid;

const isNavigableJobPosting = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.JobPosting && !!ref.uid;

const isNavigableJobApplication = (ref: Reference | null): boolean =>
  !!ref && ref.type === BriefingReferenceType.JobApplication && !!ref.uid;

const isNavigable = (ref: Reference | null): boolean =>
  isNavigableEngagement(ref) ||
  isNavigablePerson(ref) ||
  isNavigableTask(ref) ||
  isNavigableContract(ref) ||
  isNavigableSupplier(ref) ||
  isNavigableSystem(ref) ||
  isNavigableWorkRequest(ref) ||
  isNavigableJobPosting(ref) ||
  isNavigableJobApplication(ref);

const isHighSeverity = (item: ActionItem): boolean => (item.severity ?? BriefingSeverity.Low) === BriefingSeverity.High;

const isStuckProcess = (item: ActionItem): boolean =>
  item.category === BriefingItemCategory.Process ||
  (item.references ?? []).some((ref) => ref?.type === BriefingReferenceType.Process);

const gatingErrorMessage = (error: QueryError, intl: DailyBriefingIntlMessages): string | null => {
  const anyError = error as { extensions?: { code?: string }; message?: string; graphQLErrors?: unknown[] } | undefined;
  const code =
    anyError?.extensions?.code ??
    (anyError?.graphQLErrors?.[0] as { extensions?: { code?: string } })?.extensions?.code;
  const haystack = `${code ?? ''} ${anyError?.message ?? ''}`.toLowerCase();
  if (haystack.includes('feature_not_found') || haystack.includes('feature not found')) {
    return intl.unavailableMessage;
  }
  if (haystack.includes('forbidden') || haystack.includes('not permitted') || haystack.includes('unauthorized')) {
    return intl.forbiddenMessage;
  }
  return null;
};

const ReferenceLink: React.FC<{ url: string; label: string; onNavigate?: () => void }> = ({
  url,
  label,
  onNavigate
}) => (
  <StyledReferenceLink url={url as Pathname} onClick={onNavigate}>
    <Typography variant="labelSmall" color="colorPrimary">
      {label}
    </Typography>
    <Icon name="arrow_forward" size="small" color="colorPrimary" />
  </StyledReferenceLink>
);

const referenceTarget = (
  reference: Reference,
  intl: DailyBriefingIntlMessages,
  milestoneParent?: boolean
): { url: string; label: string } | null => {
  if (isNavigableEngagement(reference)) {
    return { url: `/profile/${reference.personUid}?engagementUid=${reference.uid}`, label: intl.openEngagement };
  }
  if (isNavigablePerson(reference)) {
    return { url: `/profile/${reference.uid}`, label: intl.openProfile };
  }
  if (isNavigableTask(reference)) {
    return { url: `/all-tasks/${reference.uid}`, label: intl.openTask };
  }
  if (isNavigableContract(reference)) {
    return milestoneParent
      ? { url: `/contracts/${reference.uid}/milestones`, label: intl.openMilestones }
      : { url: `/contracts/${reference.uid}`, label: intl.openContract };
  }
  if (isNavigableSupplier(reference)) {
    return { url: `/supplier/${reference.uid}`, label: intl.openSupplier };
  }
  if (isNavigableSystem(reference)) {
    return { url: '/settings/processes/instances', label: intl.openProcessInstances };
  }
  if (isNavigableWorkRequest(reference)) {
    return { url: `/work-requests/${reference.uid}`, label: intl.openWorkRequest };
  }
  if (isNavigableJobPosting(reference)) {
    return { url: `/job-postings/${reference.uid}`, label: intl.openJobPosting };
  }
  if (isNavigableJobApplication(reference)) {
    return { url: `/job-applications/${reference.uid}`, label: intl.openJobApplication };
  }
  return null;
};

const DailyBriefingReference: React.FC<{
  reference: Reference;
  intl: DailyBriefingIntlMessages;
  milestoneParent?: boolean;
}> = ({ reference, intl, milestoneParent }) => {
  const { track } = useMixpanel();
  const target = referenceTarget(reference, intl, milestoneParent);
  if (!target) {
    return null;
  }
  const handleNavigate = () =>
    track<DailyBriefingOpenReferenceDetails>(MixpanelEvents.dailyBriefing.OPEN_REFERENCE, {
      referenceType: reference.type ?? 'UNKNOWN'
    });
  return <ReferenceLink url={target.url} label={target.label} onNavigate={handleNavigate} />;
};

const DailyBriefingItem: React.FC<{ item: ActionItem; intl: DailyBriefingIntlMessages }> = ({ item, intl }) => {
  const [showAllReferences, setShowAllReferences] = useState(false);
  const category = item.category ?? BriefingItemCategory.Other;
  const severity = item.severity ?? BriefingSeverity.Low;
  const references = (item.references ?? []).filter((ref): ref is Reference => isNavigable(ref));
  const milestoneParent = (item.references ?? []).some((ref) => ref?.type === BriefingReferenceType.Milestone);
  const missingInformation = (item.missingInformation ?? []).filter((info): info is string => !!info);
  const waitingOn = waitingOnLabels(item, intl);
  const visibleReferences = showAllReferences ? references : references.slice(0, REFERENCE_LIMIT);
  const hasMoreReferences = references.length > REFERENCE_LIMIT;

  return (
    <StyledItem data-test="daily-briefing-item" $severity={severity}>
      <StyledItemHeader>
        <StyledRank>{item.rank}</StyledRank>
        <Chip size="small" color={SEVERITY_CHIP_COLOR[severity]} label={severityLabel(intl, severity)} />
        <Chip size="small" variant="outlined" color={CATEGORY_COLOR[category]} label={categoryLabel(intl, category)} />
        {waitingOn.length > 0 && (
          <span data-test="daily-briefing-waiting-on">
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`${intl.waitingOn}: ${waitingOn.join(', ')}`}
            />
          </span>
        )}
      </StyledItemHeader>

      <Typography variant="body1Bold">{item.title}</Typography>
      {item.explanation && (
        <Typography variant="body2" color="colorOnSurfaceVariant">
          {item.explanation}
        </Typography>
      )}

      {item.recommendedAction && (
        <StyledNextStep>
          <Typography variant="labelSmall" color="colorPrimary">
            {intl.recommendedAction}
          </Typography>
          <Typography variant="body2">{item.recommendedAction}</Typography>
        </StyledNextStep>
      )}

      {missingInformation.length > 0 && (
        <StyledNeeds>
          <Typography variant="labelSmall" color="colorOnSurfaceVariant">
            {intl.missingInformation}:
          </Typography>
          {missingInformation.map((info) => (
            <Typography key={info} variant="labelSmall" color="colorOnSurfaceVariant">
              {info}
            </Typography>
          ))}
        </StyledNeeds>
      )}

      {references.length > 0 && (
        <StyledReferences>
          {visibleReferences.map((reference) => (
            <DailyBriefingReference
              key={`${reference.type}-${reference.uid}`}
              reference={reference}
              intl={intl}
              milestoneParent={milestoneParent}
            />
          ))}
          {hasMoreReferences && (
            <Button
              variant="text"
              size="small"
              endIcon={showAllReferences ? 'expand_less' : 'expand_more'}
              dataTest="daily-briefing-references-toggle"
              onClick={() => setShowAllReferences((prev) => !prev)}
            >
              {showAllReferences ? intl.showLess : `${references.length} ${intl.engagementsAffected}`}
            </Button>
          )}
        </StyledReferences>
      )}
    </StyledItem>
  );
};

const DailyBriefingSummary: React.FC<{
  summary?: string | null;
  items: ActionItem[];
  intl: DailyBriefingIntlMessages;
}> = ({ summary, items, intl }) => {
  const highCount = items.filter(isHighSeverity).length;
  const processCount = items.filter(isStuckProcess).length;

  return (
    <StyledSummary data-test="daily-briefing-summary">
      <Typography variant="label1">{intl.summaryHeading}</Typography>
      <StyledSummaryCounts>
        <Chip size="small" variant="outlined" label={intl.summaryItems({ count: items.length })} />
        {highCount > 0 && <Chip size="small" color="warning" label={intl.summaryHighPriority({ count: highCount })} />}
        {processCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            color="success"
            label={intl.summaryStuckProcesses({ count: processCount })}
          />
        )}
      </StyledSummaryCounts>
      <Typography variant="labelSmall" color="colorOnSurfaceVariant">
        {intl.summaryPrioritizedNote}
      </Typography>
      {summary && (
        <Typography variant="body2" color="colorOnSurfaceVariant">
          {summary}
        </Typography>
      )}
    </StyledSummary>
  );
};

interface Suggestion {
  label: string;
  prompt?: string;
  boxText?: string;
}

interface SuggestionGroup {
  heading: string;
  suggestions: Suggestion[];
}

const buildSuggestionGroups = (intl: DailyBriefingIntlMessages): SuggestionGroup[] => [
  {
    heading: intl.groupPeople,
    suggestions: [
      { label: intl.suggestOnboarding, prompt: 'Which workers are stuck in onboarding, most delayed first?' },
      { label: intl.suggestEnding, prompt: 'Which assignments are ending soon?' },
      { label: intl.suggestMyTasks, prompt: "What's on my plate today?" }
    ]
  },
  {
    heading: intl.groupApprovals,
    suggestions: [
      { label: intl.suggestApprovals, prompt: 'What timesheet and expense approvals are pending?' },
      { label: intl.suggestProcesses, prompt: 'What processes are stuck and who must act on them?' }
    ]
  },
  {
    heading: intl.groupCompliance,
    suggestions: [
      {
        label: intl.suggestCompliance,
        prompt: 'Which suppliers, contracts, credentials or milestones are a compliance risk?'
      },
      { label: intl.suggestHiring, prompt: "What's stalled in hiring or recruiting?" },
      { label: intl.suggestMissingData, prompt: 'Which engagements have missing or incomplete data?' }
    ]
  },
  {
    heading: intl.groupPlatform,
    suggestions: [
      { label: intl.suggestHealth, prompt: 'Is anything broken — any failed imports or stuck automations?' },
      { label: intl.suggestWorkforce, prompt: 'Which supplier or site has the most stuck workers?' }
    ]
  }
];

const overviewSuggestion = (intl: DailyBriefingIntlMessages): Suggestion => ({
  label: intl.overviewFallback,
  boxText: intl.overviewPromptText
});

const DailyBriefingSuggestions: React.FC<{
  intl: DailyBriefingIntlMessages;
  onPick: (suggestion: Suggestion) => void;
}> = ({ intl, onPick }) => (
  <>
    {buildSuggestionGroups(intl).map((group) => (
      <StyledSuggestionGroup key={group.heading}>
        <Typography variant="labelSmall" color="colorOnSurfaceVariant">
          {group.heading}
        </Typography>
        <StyledSuggestions>
          {group.suggestions.map((suggestion) => (
            <Button
              key={suggestion.label}
              variant="outlined"
              size="small"
              dataTest="daily-briefing-suggestion"
              onClick={() => onPick(suggestion)}
            >
              {suggestion.label}
            </Button>
          ))}
        </StyledSuggestions>
      </StyledSuggestionGroup>
    ))}
    <StyledOverviewFallback>
      <Button
        variant="text"
        size="small"
        dataTest="daily-briefing-overview"
        onClick={() => onPick(overviewSuggestion(intl))}
      >
        {intl.overviewFallback}
      </Button>
    </StyledOverviewFallback>
  </>
);

const DailyBriefingWelcome: React.FC<{ intl: DailyBriefingIntlMessages; onPick: (suggestion: Suggestion) => void }> = ({
  intl,
  onPick
}) => (
  <StyledWelcome data-test="daily-briefing-welcome">
    <Typography variant="body1Bold">{intl.welcomeTitle}</Typography>
    <Typography variant="body2" color="colorOnSurfaceVariant">
      {intl.welcomeSubtitle}
    </Typography>
    <DailyBriefingSuggestions intl={intl} onPick={onPick} />
  </StyledWelcome>
);

const DailyBriefingExplore: React.FC<{ intl: DailyBriefingIntlMessages; onPick: (suggestion: Suggestion) => void }> = ({
  intl,
  onPick
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <StyledExplore data-test="daily-briefing-explore">
      <Button
        variant="text"
        size="small"
        dataTest="daily-briefing-explore-toggle"
        endIcon={expanded ? 'expand_less' : 'expand_more'}
        onClick={() => setExpanded((value) => !value)}
      >
        {intl.exploreAnotherArea}
      </Button>
      {expanded && <DailyBriefingSuggestions intl={intl} onPick={onPick} />}
    </StyledExplore>
  );
};

const DailyBriefingPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const intl = useDailyBriefingIntl();
  const { track } = useMixpanel();
  const [prompt, setPrompt] = useState('');
  const [hasRequested, setHasRequested] = useState(false);
  const suppressSnackbar = useCallback(() => undefined, []);
  const { data, loading, error, get } = useTypedQuery(GetDailyBriefingDocument, { onError: suppressSnackbar });

  const openTrackedRef = useRef(false);
  useEffect(() => {
    if (openTrackedRef.current) {
      return;
    }
    openTrackedRef.current = true;
    track(MixpanelEvents.dailyBriefing.OPEN);
  }, [track]);

  const requestBriefing = useCallback(
    (text?: string, source: DailyBriefingGenerateSource = 'prompt') => {
      setHasRequested(true);
      const trimmedPrompt = text?.trim() || undefined;
      // Never send the prompt text to analytics (PII) — only whether one was supplied.
      track<DailyBriefingGenerateDetails>(MixpanelEvents.dailyBriefing.GENERATE, {
        source,
        hasPrompt: !!trimmedPrompt
      });
      return get({ variables: { request: { prompt: trimmedPrompt } } });
    },
    [get, track]
  );

  const runSuggestion = useCallback(
    (suggestion: Suggestion) => {
      setPrompt(suggestion.boxText ?? suggestion.prompt ?? '');
      requestBriefing(suggestion.prompt, suggestion.prompt ? 'suggestion' : 'overview');
    },
    [requestBriefing]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      requestBriefing(prompt);
    },
    [prompt, requestBriefing]
  );

  const briefing = data?.dailyBriefing;
  const actionItems = (briefing?.actionItems ?? []).filter((item): item is ActionItem => !!item);
  const isInitialLoad = loading || (!data && !error);
  const gatingMessage = error ? gatingErrorMessage(error, intl) : null;

  const renderBody = () => {
    if (!hasRequested) {
      return <DailyBriefingWelcome intl={intl} onPick={runSuggestion} />;
    }

    if (isInitialLoad) {
      return (
        <StyledCentered data-test="daily-briefing-loading">
          <Loading />
          <Typography variant="body1Bold">{intl.generatingTitle}</Typography>
          <Typography variant="body2" color="colorOnSurfaceVariant">
            {intl.generatingHint}
          </Typography>
        </StyledCentered>
      );
    }

    if (error) {
      return (
        <StyledCentered data-test="daily-briefing-error">
          <Typography variant="body2">{gatingMessage ?? intl.errorMessage}</Typography>
          {!gatingMessage && (
            <Button variant="outlined" size="small" onClick={() => requestBriefing(prompt)}>
              {intl.retry}
            </Button>
          )}
        </StyledCentered>
      );
    }

    if (actionItems.length === 0) {
      return (
        <StyledCentered data-test="daily-briefing-empty">
          <Typography variant="body2">{briefing?.summary || intl.emptyMessage}</Typography>
          <Button variant="outlined" size="small" onClick={() => requestBriefing(prompt)}>
            {intl.retry}
          </Button>
        </StyledCentered>
      );
    }

    return (
      <StyledResults>
        <DailyBriefingSummary summary={briefing?.summary} items={actionItems} intl={intl} />
        <StyledRefineHint data-test="daily-briefing-refine-hint">
          <Icon name="arrow_upward" size="small" color="colorOnSurfaceVariant" />
          <Typography variant="labelSmall" color="colorOnSurfaceVariant">
            {intl.refineHint}
          </Typography>
        </StyledRefineHint>
        {actionItems.map((item) => (
          <DailyBriefingItem key={item.rank} item={item} intl={intl} />
        ))}
        <DailyBriefingExplore intl={intl} onPick={runSuggestion} />
      </StyledResults>
    );
  };

  return (
    <StyledPanel data-test="daily-briefing-panel">
      <StyledMenuBar>
        <Typography color="colorPrimary" variant="label1">
          {intl.panelTitle}
        </Typography>
        <IconButton
          iconName="close"
          dataTest="daily-briefing-close-button"
          aria-label={intl.closePanel}
          tooltipText={intl.closePanel}
          onClick={onClose}
        />
      </StyledMenuBar>
      <StyledPromptForm onSubmit={handleSubmit}>
        <Input
          dataTest="daily-briefing-prompt-input"
          aria-label={intl.promptLabel}
          placeholder={intl.promptPlaceholder}
          value={prompt}
          fullWidth
          onChange={(event) => setPrompt(event.target.value)}
        />
        <Button
          variant="contained"
          size="small"
          type="submit"
          dataTest="daily-briefing-submit-button"
          disabled={loading}
        >
          {intl.promptSubmit}
        </Button>
      </StyledPromptForm>
      <StyledBody>{renderBody()}</StyledBody>
    </StyledPanel>
  );
};

export const DailyBriefingDrawer: React.FC<Props> = ({ isOpen, onClose }) => {
  if (!isOpen) {
    return null;
  }
  return (
    <ResizeDrawer>
      <DailyBriefingPanel onClose={onClose} />
    </ResizeDrawer>
  );
};

const StyledPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  border-left: 1px solid ${({ theme }) => theme.colorOutlineVariant};
  background-color: ${({ theme }) => theme.colorBackground};
`;

const StyledMenuBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.colorSurface};
  padding: 0.625rem 0.75rem;
`;

const StyledPromptForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.s};
  padding: ${({ theme }) => theme.spacingSize.s} 0.75rem;
  background-color: ${({ theme }) => theme.colorSurface};
  border-bottom: 1px solid ${({ theme }) => theme.colorOutlineVariant};
`;

const StyledBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacingSize.m} 0.75rem;
`;

const StyledCentered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacingSize.m};
  height: 100%;
  text-align: center;
`;

const StyledResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.m};
`;

const StyledExplore = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.s};
  margin-top: ${({ theme }) => theme.spacingSize.s};
  padding-top: ${({ theme }) => theme.spacingSize.s};
  border-top: 1px solid ${({ theme }) => theme.colorOutlineVariant};
`;

const StyledRefineHint = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.xs};
  margin-top: -${({ theme }) => theme.spacingSize.xs};
`;

const StyledWelcome = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.s};
`;

const StyledSuggestionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.xs};
`;

const StyledSuggestions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.s};
  margin-top: ${({ theme }) => theme.spacingSize.xs};
`;

const StyledOverviewFallback = styled.div`
  margin-top: ${({ theme }) => theme.spacingSize.xs};
  padding-top: ${({ theme }) => theme.spacingSize.s};
  border-top: 1px solid ${({ theme }) => theme.colorOutlineVariant};
`;

const StyledSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.s};
  padding: ${({ theme }) => theme.spacingSize.m};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colorSurfaceContainerLow};
`;

const StyledSummaryCounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacingSize.xs};
`;

const StyledItem = styled.div<{ $severity: BriefingSeverity }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.s};
  padding: ${({ theme }) => theme.spacingSize.m};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colorOutlineVariant};
  border-left: 3px solid
    ${({ theme, $severity }) =>
      $severity === BriefingSeverity.High
        ? theme.colorWarning
        : $severity === BriefingSeverity.Medium
          ? theme.colorInformational
          : theme.colorOutlineVariant};
  background-color: ${({ theme }) => theme.colorSurface};
`;

const StyledItemHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.xs};
`;

const StyledRank = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 ${({ theme }) => theme.spacingSize.xs};
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colorSurfaceContainer};
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const StyledNextStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${({ theme }) => theme.spacingSize.s};
  border-left: 3px solid ${({ theme }) => theme.colorPrimary};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colorSurfaceContainerLow};
`;

const StyledNeeds = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacingSize.xs};
`;

const StyledReferences = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.s};
  margin-top: ${({ theme }) => theme.spacingSize.xs};
`;

const StyledReferenceLink = styled(AppLink)`
  && {
    gap: ${({ theme }) => theme.spacingSize.xs};
  }
`;
